import React, { FC, createContext, useCallback, useEffect } from "react";
import { useAuthState } from "./hooks/useAuthState";
import { useFirebaseAuth } from "./hooks/useFirebaseAuth";
import { useDataFetching } from "./hooks/useDataFetching";
import { useNavigation } from "./hooks/useNavigation";
import LoadingSpinner from "@/components/ui/animations/Loading";
import { useSocketManagement } from "./hooks/useSocketManagement";

interface AuthContextProps {
  loading: boolean;
  user: any | null;
  refetchUserData: () => Promise<void>;
}

interface AuthProviderProps {
  children?: React.ReactNode;
}

export const AuthContext = createContext<AuthContextProps>({
  loading: true,
  user: null,
  refetchUserData: async () => {},
});

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const {
    authUser,
    currentUser,
    organizationId,
    organization,
    loading,
    authInitialized,
    authLoading,
    dataLoading,
    setAuth,
    setDataLoadingState,
  } = useAuthState();

  const { fetchEssentialData, fetchOrganizationData, refetchUserData } =
    useDataFetching();

  const handleAuthStateChanged = useCallback(
    async (user: any | null) => {
      console.log("Auth state changed:", user);
      setAuth(user); 
      
      if (user) {
        setDataLoadingState(true);
        try {
          // Fetch essential data first
          const result = await fetchEssentialData(user);
          if (!result.success) {
            console.error("Failed to fetch essential data");
          }
        } catch (error) {
          console.error("AuthProvider: Failed to fetch user data.", error);
        } finally {
          setDataLoadingState(false);
        }
      }
    },
    [fetchEssentialData, setAuth, setDataLoadingState]
  );

  const handleAuthError = useCallback(
    (error: any) => {
      console.error("Firebase authentication error:", error);
      setAuth(null);
    },
    [setAuth]
  );

  useEffect(() => {
    if (organizationId && authUser && !dataLoading && authInitialized) {
      setDataLoadingState(true);
      fetchOrganizationData().finally(() => {
        setDataLoadingState(false);
      });
    }
  }, [organizationId, authUser, authInitialized]); 

  const handleRefetchUserData = useCallback(async () => {
    await refetchUserData(authUser, organizationId, organization);
  }, [refetchUserData, authUser, organizationId, organization]);

  useFirebaseAuth({
    onAuthStateChanged: handleAuthStateChanged,
    onAuthError: handleAuthError,
  });

  useSocketManagement();
  useNavigation({
    loading,
    authUser,
    currentUser,
    organizationId,
    dataLoading,
    authInitialized,
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  const value = {
    loading,
    user: authUser,
    refetchUserData: handleRefetchUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};